import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider } from "@tanstack/react-router";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { queryClient } from "./lib/query-client";
import { router } from "./router";
import { installTelemetry } from "./lib/telemetry";
import "./index.css";

installTelemetry(router);

const root = document.getElementById("root");
if (!root) throw new Error("Missing #root element");

/**
 * Pages are separate chunks (router.tsx, lazyRouteComponent). Fetch the chunks
 * for the current URL before React takes over #root: until then the
 * prerendered HTML stays on screen, instead of a blank Suspense gap between
 * React clearing it and the page code arriving. A failed or slow preload never
 * blocks the app for more than a moment.
 */
async function preloadCurrentRoute(): Promise<void> {
  try {
    const { matchedRoutes } = router.getMatchedRoutes(window.location.pathname);
    const loads = matchedRoutes.map((r) => (r.options.component as { preload?: () => Promise<void> } | undefined)?.preload?.());
    await Promise.race([Promise.all(loads), new Promise((resolve) => setTimeout(resolve, 3000))]);
  } catch {
    // Render anyway; the router loads the chunk itself.
  }
}

void preloadCurrentRoute().then(() => {
  createRoot(root).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </StrictMode>,
  );
});
